/**
 * The waits a recipe's steps state in words: « mariner 15 à 30 minutes »,
 * « laisser lever 1 à 2 heures », « toute la nuit », "bake 3 hours". A model
 * writes the step timeline; this finder is the check that the timeline kept
 * every wait the recipe itself printed (#850).
 *
 * Pure and bilingual, no imports from React Native or Firebase beyond the step
 * reader and the HTML stripper, so server code under api/_lib/ can import it too.
 *
 * A wait is a sentence with a wait verb AND a duration (or an overnight
 * phrase). `away` waits leave the cook free (marinate, chill, rise), `home`
 * waits keep them in the kitchen (oven, braise, simmer). Every word is matched
 * with a letter boundary rather than `\b` (which does not know accents) and
 * never as a substring: the repo once read "champignons" as "ham".
 *
 * Day-long waits ("2 days", « 2 jours ») are not parsed; only minutes and hours,
 * plus an overnight phrase. In the bundled catalogue nearly every day figure is
 * a storage or make-ahead note, not a wait.
 *
 * Two questions, two finders. `findStatedWaits` asks whether a timeline dropped
 * a wait, so it stays strict: « cuire 45 minutes » may be stove work the model
 * rightly calls active. `findWrittenWaits` asks whether a wait the timeline kept
 * is written anywhere, so it reads every way a recipe prints a time.
 */
import { stepTextOf } from './stepText';
import { htmlToPlainText } from './stripInstructionHtml';

export interface StatedWait {
  /** Index of the step in the recipe's step list. */
  step: number;
  /** Minutes. */
  min: number;
  max: number;
  kind: 'away' | 'home';
}

/**
 * Page text is whatever a site chooses to send, and these patterns are quadratic
 * on a run of blanks. Blanks collapse to one (a line break stays: it ends a
 * sentence), and each input is cut to a length no real one reaches.
 */
const MAX_DURATION_TEXT = 200;
const MAX_STEP_TEXT = 3000;
export const collapseBlanks = (text: string): string => text.replace(/[^\S\n]+/g, ' ').replace(/\n\s*/g, '\n');

/** Under this a duration is a cooking time, not a wait. */
const MIN_WAIT_MINUTES = 10;
const OVERNIGHT_RANGE = { min: 480, max: 720 };

const START = '(?:^|[^\\p{L}\\d])';
const NUM =
  '(?:\\d+\\s+\\d+/\\d+|\\d+/\\d+|\\d+(?:[.,]\\d+)?\\s?[½¼¾]?|[½¼¾]|une?|one|an|deux|two|trois|three)';
const UNIT = '(?:heures?|hours?|hrs?|h|minutes?|mins?|mn)(?![\\p{L}])';
const SEP = '\\s*(?:-|–|—|à|to|or|ou|et|and)\\s*';

type Reader = (m: RegExpExecArray) => [number, number] | null;

const flags = 'giu';
// Two-part forms first: « 1 h 30 », « 1h30 », « 1 hour 30 minutes », "1 hour and 15 minutes".
// "2 to 3 hours and 30 minutes" adds the minutes to both ends.
const HOURS_MINUTES: [RegExp, Reader][] = [
  [
    new RegExp(`${START}(${NUM})(?:${SEP}(${NUM}))?\\s*(?:heures?|hours?|hrs?|h)(?![\\p{L}])\\s*(?:(?:and|et)\\s*)?(\\d{1,2})\\s*(?:minutes?|mins?|mn)(?![\\p{L}])`, flags),
    m => {
      const a = amountOf(m[1]);
      const b = m[2] ? amountOf(m[2]) : a;
      if (Number.isNaN(a) || Number.isNaN(b) || a > b) return null;
      const extra = parseInt(m[3], 10);
      return [Math.round(a * 60) + extra, Math.round(b * 60) + extra];
    },
  ],
  [
    new RegExp(`${START}(\\d+)\\s*(?:h|heures?|hours?)\\s*(\\d{2})(?!\\d)`, flags),
    m => {
      const total = parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
      return [total, total];
    },
  ],
  // « une heure et demie », « 1 heure et quart », "an hour and a half".
  [
    new RegExp(`${START}(${NUM})\\s*(?:heures?|hours?|hrs?|h)(?![\\p{L}])\\s*(?:et|and)\\s*(?:a\\s+)?(demie?|half|quart|quarter)(?![\\p{L}])`, flags),
    m => {
      const a = amountOf(m[1]);
      if (Number.isNaN(a)) return null;
      const total = Math.round(a * 60) + (/^(?:dem|hal)/i.test(m[2]) ? 30 : 15);
      return [total, total];
    },
  ],
  // "one and a half hours".
  [
    new RegExp(`${START}(${NUM})\\s+and\\s+a\\s+half\\s*(?:hours?|hrs?)(?![\\p{L}])`, flags),
    m => {
      const a = amountOf(m[1]);
      if (Number.isNaN(a)) return null;
      const total = Math.round(a * 60) + 30;
      return [total, total];
    },
  ],
];
const RANGE = new RegExp(`${START}(${NUM})\\s*(${UNIT})?${SEP}(${NUM})\\s*(${UNIT})`, flags);
const SINGLE = new RegExp(`${START}(${NUM})\\s*(${UNIT})`, flags);
const HALF_HOUR = new RegExp(`${START}(?:demi-heure|half[ -](?:an[ -])?hour)(?![\\p{L}])`, flags);

const WORDS: Record<string, number> = {
  un: 1, une: 1, one: 1, an: 1, deux: 2, two: 2, trois: 3, three: 3,
};

function amountOf(raw: string): number {
  const s = raw.trim().toLowerCase();
  if (s in WORDS) return WORDS[s];
  const slash = s.match(/^(?:(\d+)\s+)?(\d+)\/(\d+)$/);
  if (slash) return Number(slash[1] ?? 0) + Number(slash[2]) / Number(slash[3]);
  const m = s.match(/^(\d+(?:[.,]\d+)?)?\s*([½¼¾])?$/);
  if (!m) return NaN;
  const whole = m[1] ? parseFloat(m[1].replace(',', '.')) : 0;
  const fraction = m[2] === '½' ? 0.5 : m[2] === '¼' ? 0.25 : m[2] === '¾' ? 0.75 : 0;
  return whole + fraction;
}

const minutesPer = (unit: string): number => (unit.toLowerCase().startsWith('h') ? 60 : 1);

interface Found {
  start: number;
  end: number;
  min: number;
  max: number;
}

/** The first duration at or after `from`; the earliest start wins, and on a tie the two-part forms, then range, then single. */
function nextDuration(text: string, from: number): Found | null {
  const forms: [RegExp, Reader][] = [
    ...HOURS_MINUTES,
    [
      RANGE,
      m => {
        const a = amountOf(m[1]);
        const b = amountOf(m[3]);
        if (Number.isNaN(a) || Number.isNaN(b)) return null;
        const second = minutesPer(m[4]);
        const first = m[2] ? minutesPer(m[2]) : second;
        const min = Math.round(a * first);
        const max = Math.round(b * second);
        // "1 hour and 15 minutes" is a sum, not a range; the two-part form takes it.
        return min > max ? null : [min, max];
      },
    ],
    [
      SINGLE,
      m => {
        const a = amountOf(m[1]);
        if (Number.isNaN(a)) return null;
        const total = Math.round(a * minutesPer(m[2]));
        return [total, total];
      },
    ],
    [HALF_HOUR, () => [30, 30]],
  ];
  let best: Found | null = null;
  for (const [re, read] of forms) {
    re.lastIndex = from;
    const m = re.exec(text);
    const minutes = m ? read(m) : null;
    if (m && minutes && (!best || m.index < best.start)) {
      best = { start: m.index, end: m.index + m[0].length, min: minutes[0], max: minutes[1] };
    }
  }
  return best;
}

/** Every duration in `text`, left to right. */
function allDurations(text: string): Found[] {
  const found: Found[] = [];
  let from = 0;
  for (let next = nextDuration(text, from); next; next = nextDuration(text, from)) {
    found.push(next);
    from = next.end;
  }
  return found;
}

/**
 * The first duration written in `text`, in minutes: « 15 à 30 minutes »,
 * "2 to 3 hours", « 1 h 30 », « 1½ heure ». A single figure is a range of one.
 * No duration → null. (The 10-minute floor belongs to `findStatedWaits`.)
 */
export function parseDurationRange(text: string): { min: number; max: number } | null {
  const first = nextDuration(collapseBlanks(text).slice(0, MAX_DURATION_TEXT), 0);
  return first ? { min: first.min, max: first.max } : null;
}

const edge = (alternatives: string) =>
  new RegExp(`${START}(?:${alternatives})(?![\\p{L}])`, flags);

const MARINADE_WORDS = [
  'marinat(?:e|es|ed|ing|ion|ions)', 'marinades?', 'mariner', 'marinez', 'marinent', 'marinée?s?', 'marinés?',
];

const AWAY_VERBS = edge([
  ...MARINADE_WORDS,
  'refrigerat\\p{L}*', 'réfrigér\\p{L}*', 'fridge', 'frigo', 'chill(?:s|ed|ing)?', 'refroidi\\p{L}*',
  'ris(?:e|es|ing|en)', 'proof(?:s|ed|ing)?',
  '(?:laiss\\p{L}*|faire|faites)\\s+(?:lever|pousser)', 'levée',
  'rest(?:s|ed|ing)?(?![\\p{L}])(?!\\s+of\\b)', 'repos\\p{L}*',
  'soak(?:s|ed|ing)?', 'tremp\\p{L}*',
  'cure|cures|curing',
  'freez(?:e|es|ing|er)', 'congeler', 'congelez', 'congélation', 'congélateur', 'surgeler', 'surgelez',
  'slow[ -]cook\\p{L}*', 'crock[ -]?pot', 'mijoteuse', 'cuisson lente',
].join('|'));

const HOME_VERBS = edge([
  'bak(?:e|es|ed|ing)(?![\\p{L}])(?!\\s+(?:sheet|powder|soda|dish|pan|paper|tray|mat)s?\\b)',
  'roast(?:s|ing)?', 'rôtir', 'rôtissez', 'enfourn\\p{L}*', 'au four',
  'brais(?:e|es|ed|ing)', 'braiser', 'braisez',
  'simmer(?:s|ed|ing)?', 'mijot(?:er|ez|e|ent)',
].join('|'));

/** Words that print a time `findStatedWaits` must not read as a wait, and `findWrittenWaits` must read. */
const COOKING_VERBS = edge([
  'cuire', 'cuis(?:ez|ent)', 'cuisson', 'cook(?:s|ed|ing)?', 'du four', 'oven',
  'bouillir', 'boil(?:s|ed|ing)?', 'frémi\\p{L}*', 'réduire', 'réduisez', 'reduc(?:e|es|ed|ing)',
  'griller', 'grillez', 'grill(?:s|ed|ing)?', 'fumer', 'smok(?:e|es|ed|ing)', 'steam(?:s|ed|ing)?',
  'tiédir', 'cool(?:s|ed|ing)?', 'sit(?:s|ting)?', 'stand(?:s|ing)?', 'dry',
  'gonfl\\p{L}*', 'doubl\\p{L}*',
  'set aside', 'leave', 'let', 'wait', 'attend\\p{L}*', 'laiss\\p{L}*', 'mett(?:re|ez) de côté', 'réserv\\p{L}*',
].join('|'));
const MARINADE = edge(MARINADE_WORDS.join('|'));
const IN_ADVANCE = edge("in advance|ahead|à l['’]avance|d['’]avance");

// Storage notes, blanked out (to the end of their clause) before a sentence is
// read: "it keeps up to 3 days" beside "chill 2 hours" must not hide the chill,
// and "store for up to 48 hours" is not a wait. Imperative "Keep covered/chilled
// ... for 2 hours" is a wait, so a bare "keep" needs a subject before it.
const STORAGE = new RegExp(
  [
    `${START}(?:keeps|(?:will|can|should|may|it|they|leftovers?|this|these|that|which)\\s+(?:(?:will|can)\\s+)?keep)(?![\\p{L}])[^,;()]*`,
    `${START}stor(?:e|es|ed|ing)(?=\\s+(?:in|for|at|up|covered|tightly|refrigerated|chilled|airtight|any|leftovers?|them|it)(?![\\p{L}]))[^,;()]*`,
    `${START}se (?:conserve|garde)\\p{L}*[^,;()]*`,
    `${START}(?:jusqu['’]à|up to)\\s+\\d+\\s*(?:jours?|semaines?|mois|days?|weeks?|months?)(?![\\p{L}])`,
  ].join('|'),
  flags,
);

const OVERNIGHT = new RegExp(
  `${START}(?:overnight|toute la nuit|une nuit|la nuit|la veille|de nuit|all night|during the night)(?![\\p{L}])`,
  flags,
);

const SENTENCE_END = /(?<![.!?])[.!?]+(?=\s|$)|\n+/;

/** Where each match of one family starts and ends in the sentence. */
const spansOf = (re: RegExp, sentence: string): [number, number][] =>
  Array.from(sentence.matchAll(re), m => [m.index ?? 0, (m.index ?? 0) + m[0].length]);

// Between an overnight phrase and a duration, one of these means two waits.
const NEXT_ACTION = new RegExp(
  `${START}(?:then|and then|puis|ensuite|après|afterwards?|before|avant)(?![\\p{L}])|;`,
  'iu',
);

interface Candidate {
  min: number;
  max: number;
  at: number;
  end: number;
  overnight: boolean;
}

function waitsIn(raw: string, step: number, cooking = false): StatedWait[] {
  const sentence = raw.replace(STORAGE, ' ');
  const away = spansOf(AWAY_VERBS, sentence);
  const home = [...spansOf(HOME_VERBS, sentence), ...(cooking ? spansOf(COOKING_VERBS, sentence) : [])];
  if (away.length === 0 && home.length === 0) return [];

  // A sentence can hold two waits ("laissez lever 1 heure, puis enfournez 35 minutes").
  const found: Candidate[] = allDurations(sentence)
    .filter(d => d.max >= MIN_WAIT_MINUTES)
    .map(d => ({ min: d.min, max: d.max, at: d.start, end: d.end, overnight: false }));
  const durations = [...found];
  const verbs = [...away, ...home];
  for (const [from, to] of spansOf(OVERNIGHT, sentence)) {
    // "overnight (8 to 12 hours)" or "overnight, or at least 2 hours" is one
    // wait: same clause, no other verb or "then" between the two.
    const sameWait = (d: Candidate) => {
      const [gapFrom, gapTo] = d.at < from ? [d.end, from] : [to, d.at];
      if (NEXT_ACTION.test(sentence.slice(gapFrom, gapTo))) return false;
      return !verbs.some(([vFrom, vTo]) => vFrom >= gapFrom && vTo <= gapTo);
    };
    const twin = durations
      .filter(sameWait)
      .sort((x, y) => Math.abs(x.at - from) - Math.abs(y.at - from))[0];
    if (twin) {
      twin.min = Math.min(twin.min, OVERNIGHT_RANGE.min);
      twin.max = Math.max(twin.max, OVERNIGHT_RANGE.max);
      twin.at = Math.min(twin.at, from);
    } else {
      found.push({ ...OVERNIGHT_RANGE, at: from, end: to, overnight: true });
    }
  }
  found.sort((x, y) => x.at - y.at);

  // The verb closest to a figure says whether the cook is free during it.
  const distance = (spans: [number, number][], at: number) =>
    Math.min(Infinity, ...spans.map(([from, to]) => Math.min(Math.abs(from - at), Math.abs(to - at))));
  return found.map(({ min, max, at }) => ({
    step,
    min,
    max,
    kind: distance(home, at) < distance(away, at) ? 'home' : 'away',
  }));
}

/** What the model read: a stored step can hold markup and « 30&nbsp;min ». */
const plainStep = (raw: unknown): string => collapseBlanks(htmlToPlainText(stepTextOf(raw))).slice(0, MAX_STEP_TEXT);

/**
 * The waits written in `steps` (strings or `{ step }` objects, read through
 * `stepTextOf`), in step order. `step` is the index in `steps`.
 */
export function findStatedWaits(steps: unknown[]): StatedWait[] {
  const waits: StatedWait[] = [];
  steps.forEach((raw, step) => {
    const text = plainStep(raw);
    if (!text) return;
    for (const sentence of sentencesOf(text)) waits.push(...waitsIn(sentence, step));
  });
  return waits;
}

const sentencesOf = (text: string): string[] => text.split(SENTENCE_END).filter(s => s.trim());

/** Stored steps are sometimes one sentence cut in two: an open step, then one starting in lowercase. */
const LEFT_OPEN = /[^.!?:;)"»”\s]\s*$/u;
const RUNS_ON = /^\s*(?:\d+[.)]\s*)?(\p{Ll}.*)$/su;

/**
 * Every wait the steps print, in any words: what `findStatedWaits` finds, plus a
 * time written as cooking or cooling, a sentence split across two stored steps
 * (kept on the step holding the time), and a marinade's make-ahead allowance
 * (« up to 1 hour in advance »), which the founder counts as a marinade.
 * Evidence that a wait is written, never proof that one was dropped.
 */
export function findWrittenWaits(steps: unknown[]): StatedWait[] {
  const texts = steps.map(plainStep);
  const waits: StatedWait[] = [];
  texts.forEach((text, step) => {
    const sentences = sentencesOf(text);
    for (const sentence of sentences) waits.push(...waitsIn(sentence, step, true));

    if (spansOf(MARINADE, text).length > 0) {
      for (const sentence of sentences.filter(s => spansOf(IN_ADVANCE, s).length > 0)) {
        for (const { min, max } of allDurations(sentence)) {
          if (max >= MIN_WAIT_MINUTES) waits.push({ step, min, max, kind: 'away' });
        }
      }
    }

    const runsOn = texts[step + 1]?.match(RUNS_ON);
    if (runsOn && LEFT_OPEN.test(text)) {
      const opening = sentencesOf(runsOn[1])[0];
      waits.push(...waitsIn(`${sentences[sentences.length - 1]} ${opening}`, step + 1, true));
    }
  });
  return waits;
}
